import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ShoppingBag, Heart, Share2, Minus, Plus, Truck, RotateCcw, Shield } from 'lucide-react';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/product/ProductCard';
import toast from 'react-hot-toast';
import { getImageUrl } from '../utils/imageUrl';

interface Variant { id: number; size: string; color: string; color_hex: string; price: number; stock: number; }
interface Product {
  id: number; name: string; slug: string; price: number; compare_price?: number;
  description: string; featured_image: string; is_new?: boolean;
  variants: Variant[]; images: Array<{ id: number; image: string; alt_text: string }>;
  sizes?: string[]; colors?: Array<{ name: string; hex: string }>;
  brand?: string; material?: string; sku?: string; stock: number;
  category: { id: number; name: string; slug: string };
}

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { addItem, openCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [isWished, setIsWished] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(r => {
        setProduct(r.data.product);
        setRelated(r.data.related || []);
        if (r.data.product.sizes?.[0]) setSelectedSize(r.data.product.sizes[0]);
        if (r.data.product.colors?.[0]) setSelectedColor(r.data.product.colors[0].name);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!product || !selectedSize || !selectedColor) { setSelectedVariant(null); return; }
    const variant = product.variants.find(v => v.size === selectedSize && v.color === selectedColor);
    setSelectedVariant(variant || null);
  }, [selectedSize, selectedColor, product]);

  const handleAddToCart = () => {
    if (!product) return;
    const sizes = product.sizes || [];
    const colors = product.colors || [];
    if (sizes.length > 0 && !selectedSize) { toast.error('Please select a size'); return; }
    if (colors.length > 0 && !selectedColor) { toast.error('Please select a color'); return; }

    const color = product.colors?.find(c => c.name === selectedColor);
    addItem({
      id: Date.now(),
      productId: product.id,
      variantId: selectedVariant?.id,
      name: product.name,
      price: selectedVariant?.price || product.price,
      image: getImageUrl(product.featured_image),
      size: selectedSize || undefined,
      color: selectedColor || undefined,
      colorHex: color?.hex,
      quantity,
    });
    toast.success('Added to cart!');
    openCart();
  };

  const formatPrice = (p: number) => `Rs.${p.toLocaleString()}`;
  const discount = product?.compare_price && product.compare_price > product.price
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : 0;

  const allImages = product ? [
    getImageUrl(product.featured_image),
    ...product.images.map(i => getImageUrl(i.image))
  ].filter(Boolean) : [];

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 animate-pulse">
        <div className="bg-gray-100 aspect-square" />
        <div className="space-y-4">
          <div className="h-8 bg-gray-100 w-3/4" />
          <div className="h-6 bg-gray-100 w-1/4" />
          <div className="h-4 bg-gray-100 w-full" />
          <div className="h-4 bg-gray-100 w-full" />
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
      <h2 className="text-2xl font-serif text-gray-900">Product not found</h2>
      <Link to="/products" className="mt-4 inline-block text-gray-600 underline">Browse all products</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-500 mb-8">
        <Link to="/" className="hover:text-gray-900">Home</Link>
        <ChevronRight size={14} />
        <Link to={`/category/${product.category.slug}`} className="hover:text-gray-900">{product.category.name}</Link>
        <ChevronRight size={14} />
        <span className="text-gray-900 truncate">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16">
        {/* Images */}
        <div className="flex gap-4">
          {/* Thumbnails */}
          <div className="hidden md:flex flex-col gap-2 w-20 flex-shrink-0">
            {allImages.slice(0, 4).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`w-20 h-24 overflow-hidden border-2 transition-colors ${activeImage === i ? 'border-gray-900' : 'border-transparent'}`}
              >
                <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Main Image */}
          <div className="flex-1 relative overflow-hidden bg-gray-50">
            {product.is_new && <span className="absolute top-4 left-4 bg-gray-900 text-white text-xs px-2 py-1 z-10">New</span>}
            {discount > 0 && <span className="absolute top-4 left-4 bg-red-600 text-white text-xs px-2 py-1 z-10">-{discount}%</span>}
            <img
              src={allImages[activeImage] || product.featured_image}
              alt={product.name}
              className="w-full aspect-[3/4] object-cover"
            />

            {/* Mobile thumbnails */}
            {allImages.length > 1 && (
              <div className="flex md:hidden gap-2 mt-3 overflow-x-auto">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImage(i)} className={`w-16 h-20 flex-shrink-0 border-2 ${activeImage === i ? 'border-gray-900' : 'border-transparent'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">{product.brand || 'BabyCub'}</p>
          <h1 className="text-2xl md:text-3xl font-serif font-semibold text-gray-900 leading-tight">{product.name}</h1>

          <div className="flex items-center gap-3 mt-3">
            {product.compare_price && product.compare_price > product.price && (
              <span className="text-base text-gray-400 line-through">{formatPrice(product.compare_price)}</span>
            )}
            <span className={`text-2xl font-bold ${discount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{formatPrice(selectedVariant?.price || product.price)}</span>
            {discount > 0 && <span className="bg-red-50 text-red-600 text-xs px-2 py-1 rounded">{discount}% OFF</span>}
          </div>

          <p className="text-xs text-green-600 mt-1 font-medium">{(selectedVariant?.stock || product.stock) > 0 ? 'In Stock' : 'Out of Stock'}</p>

          {/* Size Selector */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center mb-2">
                <label className="text-sm font-medium text-gray-900">Size: <span className="font-bold">{selectedSize}</span></label>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`min-w-[44px] h-11 px-3 border text-sm font-medium transition-all ${selectedSize === size ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-900'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color Selector */}
          {product.colors && product.colors.length > 0 && (
            <div className="mt-5">
              <label className="text-sm font-medium text-gray-900 block mb-2">Color: <span className="font-bold">{selectedColor}</span></label>
              <div className="flex flex-wrap gap-2">
                {product.colors.map(color => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(color.name)}
                    title={color.name}
                    className={`w-10 h-10 rounded-full border-4 transition-all ${selectedColor === color.name ? 'border-gray-900 scale-110' : 'border-white shadow-md hover:scale-105'}`}
                    style={{ backgroundColor: color.hex }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-6">
            <label className="text-sm font-medium text-gray-900 block mb-2">Quantity:</label>
            <div className="flex items-center border border-gray-300 w-32">
              <button aria-label="Decrease quantity" onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-10 h-11 flex items-center justify-center hover:bg-gray-50 transition-colors">
                <Minus size={14} />
              </button>
              <span className="flex-1 text-center text-sm font-medium">{quantity}</span>
              <button aria-label="Increase quantity" onClick={() => setQuantity(q => q + 1)} className="w-10 h-11 flex items-center justify-center hover:bg-gray-50 transition-colors">
                <Plus size={14} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Subtotal: {formatPrice((selectedVariant?.price || product.price) * quantity)}</p>
          </div>

          {/* CTA Buttons */}
          <div className="mt-6 space-y-3">
            <button
              onClick={handleAddToCart}
              className="w-full bg-gray-900 text-white py-4 text-sm font-semibold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-gray-700 transition-colors"
            >
              <ShoppingBag size={18} />
              Add to Cart
            </button>
            <button
              onClick={handleAddToCart}
              className="w-full border border-gray-900 text-gray-900 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-gray-900 hover:text-white transition-all"
            >
              Buy It Now
            </button>
          </div>

          {/* Share & Wishlist */}
          <div className="flex items-center gap-4 mt-4">
            <button
              onClick={() => setIsWished(!isWished)}
              className={`flex items-center gap-2 text-sm ${isWished ? 'text-red-500' : 'text-gray-500'} hover:text-red-500 transition-colors`}
            >
              <Heart size={16} className={isWished ? 'fill-current' : ''} />
              Wishlist
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              <Share2 size={16} />
              Share
            </button>
          </div>

          {/* Delivery Info */}
          <div className="mt-8 border-t border-gray-100 pt-6 space-y-3">
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <Truck size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div><span className="font-medium text-gray-900">Free Delivery</span> on orders above Rs. 10,000. Estimated 3-5 business days.</div>
            </div>
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <RotateCcw size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div><span className="font-medium text-gray-900">Easy Returns</span> within 7 days of delivery.</div>
            </div>
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <Shield size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div><span className="font-medium text-gray-900">Secure Checkout</span> Cash on delivery available.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-16 border-t border-gray-100">
        <div className="flex gap-8 border-b border-gray-100">
          {['description', 'shipping'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${activeTab === tab ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'shipping' ? 'Shipping & Return' : 'Description'}
            </button>
          ))}
        </div>
        <div className="py-8 max-w-2xl">
          {activeTab === 'description' ? (
            <div>
              {product.sku && <p className="text-sm text-gray-500 italic mb-4">Style#{product.sku}</p>}
              <div className="prose prose-sm text-gray-600">
                {product.description ? (
                  <p>{product.description}</p>
                ) : (
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    <li>Premium quality fabric</li>
                    <li>Comfortable fit for everyday wear</li>
                    <li>Machine wash and dry</li>
                    <li>100% Cotton</li>
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm text-gray-600">
              <p><strong>Shipping:</strong> We offer free delivery on orders above Rs. 2000. Standard delivery charges of Rs. 200 apply for orders below this amount.</p>
              <p><strong>Delivery Time:</strong> Orders are typically delivered within 3-5 business days across Pakistan.</p>
              <p><strong>Returns:</strong> We accept returns within 7 days of delivery. Items must be unused and in original packaging.</p>
            </div>
          )}
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="mt-16 border-t border-gray-100 pt-12">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 mb-8 text-center">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
